import { FC, useEffect, useMemo, useRef } from 'react';
import TableModule from './TableModule';
import {
  CoreInfoRelateNames,
  isCollaborationModeDiscount,
  isCollaborationModeHighValue,
  isCollaborationModeRent,
  LeaseCostConfig,
  LeaseCostNames,
  ModuleConfig,
  SaleTargetNames
} from '../ts-config';
import { Form } from 'antd';
import { deepCopy, isArray, isNotEmpty } from '@lhb/func';
import {
  fieldNameLeaseCost,
  fieldNameSaleTarget
} from './fieldname';
import { fixIntParam } from './transfer2value';

/**
 * @Description 租赁费用成本
 */
const LeaseCost: FC<{
  form: any;
  formItemData: any;
  disabled?: boolean;
  collaborationModeOptions: any;
  onChange: any;
}> = ({
  form,
  formItemData,
  disabled,
  collaborationModeOptions,
  onChange
}) => {
  const tableRef: any = useRef(null);

  // 定制组件之外的「合作模式」的值
  const collaborationMode: any = Form.useWatch(
    CoreInfoRelateNames.collaborationMode,
    form
  );

  // 租赁成本（第一年）
  const leaseCostFirst: any = Form.useWatch(
    fieldNameLeaseCost(true, LeaseCostNames.leaseCost),
    form
  );
  // 租赁成本（第二年）
  const leaseCostSecond: any = Form.useWatch(
    fieldNameLeaseCost(false, LeaseCostNames.leaseCost),
    form
  );

  // 租金（第一年）
  const rentFirst: any = Form.useWatch(
    fieldNameLeaseCost(true, LeaseCostNames.rent),
    form
  );
  // 租金（第二年）
  const rentSecond: any = Form.useWatch(
    fieldNameLeaseCost(false, LeaseCostNames.rent),
    form
  );

  // 物业管理费（第一年）
  const propertyManagementFeeFirst: any = Form.useWatch(
    fieldNameLeaseCost(true, LeaseCostNames.propertyManagementFee),
    form
  );

  // 物业管理费（第二年）
  const propertyManagementFeeSecond: any = Form.useWatch(
    fieldNameLeaseCost(false, LeaseCostNames.propertyManagementFee),
    form
  );

  // 商场联营扣点（第一年）
  const discountRateFirst: any = Form.useWatch(
    fieldNameLeaseCost(true, LeaseCostNames.discountRate),
    form
  );
  // 商场联营扣点（第二年）
  const discountRateSecond: any = Form.useWatch(
    fieldNameLeaseCost(false, LeaseCostNames.discountRate),
    form
  );

  // 吊牌销售总金额（第一年）
  const saleAmountFirst: any = Form.useWatch(
    fieldNameSaleTarget(true, SaleTargetNames.saleAmount),
    form
  );
  // 吊牌销售总金额（第二年）
  const saleAmountSecond: any = Form.useWatch(
    fieldNameSaleTarget(false, SaleTargetNames.saleAmount),
    form
  );

  /**
   * @description 租赁成本
   */
  useEffect(() => {
    const name = fieldNameLeaseCost(true, LeaseCostNames.leaseCost);
    const val: any = calcLeaseCost(collaborationMode, rentFirst, propertyManagementFeeFirst, discountRateFirst, saleAmountFirst);
    form.setFieldValue(name, val);
  }, [collaborationMode, rentFirst, propertyManagementFeeFirst, discountRateFirst, saleAmountFirst]);

  useEffect(() => {
    const name = fieldNameLeaseCost(false, LeaseCostNames.leaseCost);
    const val: any = calcLeaseCost(collaborationMode, rentSecond, propertyManagementFeeSecond, discountRateSecond, saleAmountSecond);
    form.setFieldValue(name, val);
  }, [collaborationMode, rentSecond, propertyManagementFeeSecond, discountRateSecond, saleAmountSecond]);

  /**
   * @description 租赁成本
   * @param collaborationMode 合作模式
   * @param rent 租金
   * @param propertyManagementFee 物业管理费
   * @param discountRate 扣点值
   * @param saleAmount 吊牌销售总金额
   * @return
   *
   * 租赁：租金+物业管理费
    扣点：吊牌销售总金额*扣点值
    取高：比较「租赁」和「扣点」，两者取高
    注意：「合作模式」是定制组件之外的字段，此处取值来自「合作模式」的值
   */
  const calcLeaseCost = (collaborationMode, rent, propertyManagementFee, discountRate, saleAmount) => {
    let val: any = null;
    const selOpt = collaborationModeOptions.find((itm: any) => itm.id === collaborationMode);
    if (isCollaborationModeDiscount(selOpt?.name)) { // 扣点
      val = calcModeDiscount({ saleAmount, discountRate });
    } else if (isCollaborationModeHighValue(selOpt?.name)) { // 取高
      val = calcModeHighValue({ rent, propertyManagementFee, discountRate, saleAmount });
    } else { // 选择租赁、及未选时，都当作选中租赁
      val = calcModeRent({ rent, propertyManagementFee });
    }
    return val;
  };

  /**
   * @description 租赁成本计算：合作模式为【租赁】时
   *    租赁：租金+物业管理费
   * @param rent 租金
   * @param propertyManagementFee 物业管理费
   * @return
   */
  const calcModeRent = ({ rent, propertyManagementFee }) => {
    let val: any = null;
    isNotEmpty(rent) && (val += (+rent));
    isNotEmpty(propertyManagementFee) && (val += (+propertyManagementFee));

    isNotEmpty(val) && (val = fixIntParam(val));
    return val;
  };

  /**
   * @description 租赁成本计算：合作模式为【扣点】时
   *    扣点：吊牌销售总金额*扣点值
   * @param saleAmount
   * @param discountRate
   * @return
   */
  const calcModeDiscount = ({ saleAmount, discountRate }) => {
    return isNotEmpty(saleAmount) && isNotEmpty(discountRate) ? fixIntParam((+saleAmount) * (+discountRate) / 100) : null;
  };

  /**
   * @description 租赁成本计算：合作模式为【取高】时
   * @param rent 租金
   * @param propertyManagementFee 物业管理费
   * @param discountRate 扣点值
   * @param saleAmount 吊牌销售总金额
   * @return
   *
   * 租赁：租金+物业管理费
    扣点：吊牌销售总金额*扣点值
    取高：比较「租赁」和「扣点」，两者取高
   */
  const calcModeHighValue = ({ rent, propertyManagementFee, discountRate, saleAmount }) => {
    let val: any = null;

    const leaseVal: any = calcModeRent({ rent, propertyManagementFee });
    const discountVal: any = calcModeDiscount({ saleAmount, discountRate });
    if (isNotEmpty(leaseVal) && isNotEmpty(discountVal)) {
      val = Math.max(leaseVal, discountVal);
    } else if (isNotEmpty(leaseVal)) {
      val = leaseVal;
    } else if (isNotEmpty(discountVal)) {
      val = discountVal;
    }
    return val;
  };

  /**
   * @description 特殊处理，自定义组件相关的外部属性collaborationMode【合作模式】变动时，会影响到自动计算结果，需要触发计算
   */
  useEffect(() => {
    onChange && onChange();
  }, [leaseCostFirst, leaseCostSecond]);

  /**
   * @description 特殊处理，合作模式决定字段的必填与否
   *
   *  选择「租赁模式」，「租金」必填；
   *  选择「扣点模式」，「商场联营扣点」必填；
   *  选择「取高」，「租金」和「商场联营扣点」必填。
   */
  const currentConfig = useMemo(() => {
    const config: ModuleConfig = deepCopy(LeaseCostConfig);
    // 未选择时使用默认配置
    if (!collaborationMode || !isArray(collaborationModeOptions) || !collaborationModeOptions.length) return config;

    const selOpt = collaborationModeOptions.find((itm: any) => itm.id === collaborationMode);
    if (isCollaborationModeRent(selOpt?.name)) { // 租赁
      config.data = config.data.map((itm: any) => itm.key === LeaseCostNames.rent ? {
        ...itm,
        required: true,
      } : itm);
    } else if (isCollaborationModeDiscount(selOpt?.name)) { // 扣点
      config.data = config.data.map((itm: any) => itm.key === LeaseCostNames.discountRate ? {
        ...itm,
        required: true,
      } : itm);
    } else if (isCollaborationModeHighValue(selOpt?.name)) { // 取高
      config.data = config.data.map((itm: any) => [LeaseCostNames.rent, LeaseCostNames.discountRate].includes(itm.key) ? {
        ...itm,
        required: true,
      } : itm);
    }
    return config;
  }, [collaborationMode, collaborationModeOptions]);

  useEffect(() => {
    (tableRef.current as any).onload();
  }, [currentConfig]);

  return (
    <TableModule
      ref={tableRef}
      moduleConfig={currentConfig}
      form={form}
      formItemData={formItemData}
      disabled={disabled}
      onChange={onChange}
    />
  );
};

export default LeaseCost;
